import TransitionWrapper from "../common/transition-wrapper";
import { useState } from "react";
import AppDownloadModal from "../app-download/app-download-modal";
import { isWeb } from "../../utils/user-agent";
import { track } from "@amplitude/analytics-browser";
import { formatTimeWithAM } from "../../utils/date/format-date";
import { getExposeType } from "../../utils/store-expose";
import {
  getDayOfWeekIndex,
  getTomorrowDayOfWeekIndex,
} from "../../utils/reserve-time";
import { useNavigate } from "react-router-dom";
import ResponsiveCDNImage from "../common/image/responsive-cdn-image";
import ReserveConfirmBottomSheet from "../../Routes/store/components/reserve-confirm-bottom-sheet";

const ReserveSection = ({
  isTimeSaling,
  packData,
  isReserveTimeEnabled,
  isPackSoldOut,
  onClickPackPurchase,
  store,
  isTodayEnabled,
  isTomorrowEnabled,
  isOverTodayPickupTime,
  isLiked,
  onClickNoti,
  likeCount,
  reviewCount,
  packItems,
  bestReviewId,
  categoryName,
  isLoggedIn,
  onLoginRequired,
}: {
  isTimeSaling: boolean;
  packData: any;
  isReserveTimeEnabled: boolean;
  isPackSoldOut: boolean;
  onClickPackPurchase: (isTomorrow: boolean) => void;
  store: any;
  dayOfWeek: string;
  isTodayEnabled: boolean | undefined | null;
  isTomorrowEnabled: boolean | undefined | null;
  isOverTodayPickupTime: boolean | undefined | null;
  isLiked: boolean | undefined | null;
  onClickNoti: (e: React.MouseEvent) => void;
  likeCount: number;
  reviewCount?: number;
  packItems?:
    | {
        name: string;
      }[]
    | null;
  bestReviewId?: number | null;
  categoryName?: string;
  isLoggedIn: boolean;
  onLoginRequired: () => void;
}) => {
  const navigate = useNavigate();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showAppDownloadModal, setShowAppDownloadModal] = useState(false);
  const [clickedSource, setClickedSource] = useState<"card" | "button">(
    "button",
  );
  // const [isDescriptionOpen, setIsDescriptionOpen] = useState(false);
  const dayOfWeek = getDayOfWeekIndex(new Date());
  const tomorrowOfWeek = getTomorrowDayOfWeekIndex();
  const getReserveTime = () => {
    return (
      formatTimeWithAM(store?.reserveTime?.[dayOfWeek]?.start) +
      " ~ " +
      formatTimeWithAM(store?.reserveTime?.[dayOfWeek]?.end)
    );
  };
  const getReserveTimeForTomorrow = () => {
    return (
      " " +
      formatTimeWithAM(store?.reserveTime?.[tomorrowOfWeek]?.start) +
      " ~ " +
      formatTimeWithAM(store?.reserveTime?.[tomorrowOfWeek]?.end)
    );
  };
  const getReserveTimeBefore30Minutes = (isTomorrow: boolean) => {
    const dayKey = isTomorrow
      ? getTomorrowDayOfWeekIndex()
      : getDayOfWeekIndex(new Date());
    const start = store?.reserveTime?.[dayKey]?.start; // HH:MM
    if (!start) return "-";
    const [startHour, startMinute] = start.split(":").map(Number);
    const startDate = new Date();
    startDate.setHours(startHour, startMinute - 30);
    const startBefore30Minutes = `${startDate
      .getHours()
      .toString()
      .padStart(2, "0")}:${startDate.getMinutes().toString().padStart(2, "0")}`;
    return formatTimeWithAM(startBefore30Minutes);
  };

  const handlePurchaseClick = (clickedSource: "card" | "button") => {
    // 로그 트래킹 구분용입니다! 카드를 클릭했느냐, 버튼을 클릭했느냐
    setClickedSource(clickedSource);

    // 카드 클릭 시 pack 상세 페이지로 이동
    if (clickedSource === "card") {
      track("마감팩 카드 클릭", {
        매장명: store?.title as string,
        가격: packData?.pack?.userPrice,
      });
      navigate(`/pack/${store?.id}`);
      return;
    }

    // "예약" 버튼 클릭 시 웹 검사 후 로그인 검사, 그다음 예약 플로우 진행
    if (isWeb()) {
      setShowAppDownloadModal(true);
      return;
    }
    if (!isLoggedIn) {
      onLoginRequired();
      return;
    }
    setShowConfirmModal(true);
  };

  const handleConfirm = (isTomorrow: boolean) => {
    setShowConfirmModal(false);
    if (clickedSource === "card") {
      // 카드 클릭시
      track("마감팩 카드 클릭", {
        매장명: store?.title as string,
        가격: packData?.pack?.userPrice,
      });
    } else {
      // CTA 버튼 클릭시
      track("마감팩 구매하기 버튼 클릭", {
        매장명: store?.title as string,
        가격: packData?.pack?.userPrice,
      });
    }
    onClickPackPurchase(isTomorrow);
  };

  const handleAppDownloadModalClose = () => {
    setShowAppDownloadModal(false);
  };

  const exposeType = getExposeType({
    isTodayEnabled: isTodayEnabled ?? undefined,
    isTomorrowEnabled: isTomorrowEnabled ?? undefined,
    isOverTodayPickupTime: isOverTodayPickupTime ?? undefined,
  });

  return (
    <>
      {packData?.pack && (
        <div>
          <div className="w-full p-5 pt-0 pb-10 h-full relative flex flex-col bg-white">
            <div className="flex flex-col gap-y-4">
              {/* Simplified Pickup Info */}
              {exposeType === "오늘" || exposeType === "내일" ? (
                <div className="grid grid-cols-[1fr_auto_1fr] items-center bg-[#F8F9FA] rounded-xl py-4 mx-1">
                  <div className="flex flex-col justify-start items-start pl-5 pr-2 gap-y-0.5">
                    <span className="text-gray-500 text-[11px] sm:text-xs font-medium whitespace-nowrap">
                      픽업 시간
                    </span>
                    <span className="text-gray-800 font-bold text-[12px] sm:text-sm leading-tight">
                      {exposeType === "오늘"
                        ? getReserveTime()
                        : exposeType === "내일"
                          ? "내일 " + getReserveTimeForTomorrow()
                          : "-"}
                    </span>
                  </div>
                  <div className="w-[1px] h-8 bg-gray-300"></div>
                  <div className="flex flex-col justify-start items-start pl-4 pr-2 gap-y-0.5">
                    <span className="text-gray-500 text-[11px] sm:text-xs font-medium whitespace-nowrap">
                      확정 시간
                    </span>
                    <span className="text-gray-800 font-bold text-[12px] sm:text-sm leading-tight">
                      {exposeType === "오늘"
                        ? getReserveTimeBefore30Minutes(false)
                        : exposeType === "내일"
                          ? "내일 " + getReserveTimeBefore30Minutes(true)
                          : "-"}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center bg-[#F8F9FA] rounded-xl py-4 mx-1">
                  <span className="text-gray-500 text-sm">
                    오픈 준비중이에요
                  </span>
                </div>
              )}

              {/* 상품 카드 */}
              <TransitionWrapper
                scale={0.95}
                opacity={0.8}
                className="w-full flex p-3 rounded-[0.625rem] overflow-hidden border border-[#F9F9F9] bg-white shadow-[0_3px_8px_0_rgba(0,0,0,0.05)]"
                onClick={
                  exposeType !== "X" && !isPackSoldOut
                    ? () => handlePurchaseClick("card")
                    : undefined
                }
              >
                <div className="relative w-[110px] h-[110px] rounded-lg overflow-hidden shrink-0">
                  <ResponsiveCDNImage
                    src={
                      store?.img ||
                      require("../../Routes/store/assets/bread_to_bag.png") as string
                    }
                    alt="store-img"
                    className="w-full h-full object-cover"
                    sizeRatio={1 / 2}
                  />
                  {exposeType === "X" && (
                    <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                      <span className="text-white text-lg font-semibold">
                        오픈 준비중
                      </span>
                    </div>
                  )}
                  {exposeType !== "X" && isPackSoldOut && (
                    <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                      <span className="text-white text-lg font-semibold">
                        품절
                      </span>
                    </div>
                  )}
                </div>

                <div className="pl-4 w-full flex flex-col justify-between min-h-[110px]">
                  <div>
                    <div className="text-gray-900 text-lg font-semibold line-clamp-2 leading-snug">
                      {store?.title +
                        " " +
                        (exposeType === "내일" ? "내일" : "오늘") +
                        "의 " +
                        (categoryName || "빵")}
                    </div>
                    {/* 재고 표시 (임시 - 필요시 추가) */}
                    <div className="text-sm font-medium text-[#1569fc] mt-1">
                      {packData?.pack?.currentCount < 0
                        ? 0
                        : packData?.pack?.currentCount}
                      개 남았어요!
                    </div>
                  </div>

                  {exposeType !== "X" && !isPackSoldOut && (
                    <div className="mt-1 w-full flex items-center justify-between">
                      <div className="flex flex-col items-start justify-center">
                        <div className="text-sm text-red-400 line-through font-semibold">
                          {packData?.pack?.price?.toLocaleString()}원
                        </div>
                        <div className="text-lg text-black font-bold">
                          {packData?.pack?.userPrice?.toLocaleString()}원
                        </div>
                      </div>

                      <button
                        className="ml-2 px-4 py-1.5 bg-white border border-gray-300 rounded text-sm font-medium text-gray-700 whitespace-nowrap shrink-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePurchaseClick("button");
                        }}
                      >
                        예약
                      </button>
                    </div>
                  )}
                </div>
              </TransitionWrapper>

              {/* 팩 설명 제거됨 */}
            </div>
          </div>
        </div>
      )}

      <AppDownloadModal
        isOpen={showAppDownloadModal}
        onClose={handleAppDownloadModalClose}
        where="예약구매페이지"
      />

      <ReserveConfirmBottomSheet
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleConfirm}
        exposeType={exposeType}
        store={store}
      />
    </>
  );
};

export default ReserveSection;
